import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LandingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser, setHasAccess, checkAuth } = useContext(AuthContext);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showManualAuth, setShowManualAuth] = useState(false);
  const [showDemoModal, setShowDemoModal] = useState(false);
  const [demoChemical, setDemoChemical] = useState(null);

  useEffect(() => {
    // Aggressively check for session_id multiple times (mobile browsers are slow)
    const checkForSessionId = (attempt = 1) => {
      const hash = window.location.hash;
      const searchParams = new URLSearchParams(window.location.search);
      const sessionIdFromQuery = searchParams.get('session_id');
      const storedSessionId = localStorage.getItem('pending_session_id');
      
      // Samsung browser check - also try sessionStorage
      const isSamsung = /SamsungBrowser/i.test(navigator.userAgent);
      const sessionStorageId = isSamsung ? sessionStorage.getItem('pending_session_id') : null;
      
      // Also check cookie as fallback
      const cookieMatch = document.cookie.match(/pending_session_id=([^;]+)/);
      const cookieSessionId = cookieMatch ? cookieMatch[1] : null;
      
      console.log(`Auth check attempt ${attempt}:`, {
        hasHash: !!hash,
        hashContent: hash.substring(0, 50),
        hasQuery: !!sessionIdFromQuery,
        hasLocalStorage: !!storedSessionId,
        hasSessionStorage: !!sessionStorageId,
        hasCookie: !!cookieSessionId,
        isSamsung: isSamsung,
        url: window.location.href
      });
      
      if (hash && hash.includes('session_id=')) {
        console.log('Found session_id in hash');
        processSessionId(hash, 'hash');
      } else if (sessionIdFromQuery) {
        console.log('Found session_id in query params');
        processSessionId(sessionIdFromQuery, 'query');
      } else if (cookieSessionId) {
        console.log('Found session_id in cookie');
        processSessionId(cookieSessionId, 'cookie');
        // Clear cookie after use
        document.cookie = 'pending_session_id=; path=/; max-age=0';
      } else if (sessionStorageId) {
        console.log('Found session_id in sessionStorage (Samsung)');
        processSessionId(sessionStorageId, 'sessionStorage');
        sessionStorage.removeItem('pending_session_id');
      } else if (storedSessionId) {
        console.log('Found session_id in localStorage');
        processSessionId(storedSessionId, 'storage');
        localStorage.removeItem('pending_session_id');
      } else if (attempt < 5) {
        // Mobile browsers may be slow, retry up to 5 times with increasing delays
        setTimeout(() => checkForSessionId(attempt + 1), attempt * 500);
      } else {
        // After 5 attempts, show manual option for mobile users
        const isMobile = /Mobile|Android|iPhone|iPad|SamsungBrowser/i.test(navigator.userAgent);
        if (isMobile && window.location.href.includes('auth.emergentagent')) {
          console.log('Mobile user may need manual intervention');
          setShowManualAuth(true);
        }
      }
    };
    
    // Start checking immediately and with delays for mobile
    checkForSessionId(1);
  }, []);

  const processSessionId = async (source, type) => {
    setIsProcessing(true);
    try {
      let sessionId;
      
      if (type === 'hash') {
        // Extract from hash
        const cleanHash = source.startsWith('#') ? source.substring(1) : source;
        sessionId = cleanHash.split('session_id=')[1]?.split('&')[0];
      } else if (type === 'query') {
        // Already extracted from query
        sessionId = source;
      } else if (type === 'storage' || type === 'sessionStorage' || type === 'cookie') {
        // From localStorage, sessionStorage, or cookie
        sessionId = source;
      }
      
      if (!sessionId) {
        console.error('No session ID found in:', type, source);
        toast.error('Authentication link is invalid. Please try logging in again.');
        setIsProcessing(false);
        window.history.replaceState({}, document.title, '/');
        return;
      }
      
      // Decode URL-encoded characters
      sessionId = decodeURIComponent(sessionId);
      
      console.log(`Processing session ID from ${type} (length):`, sessionId.length);
      console.log('Session ID preview:', sessionId.substring(0, 20) + '...');
      console.log('User agent:', navigator.userAgent);
      console.log('Is Samsung:', /SamsungBrowser/i.test(navigator.userAgent));
      console.log('Is mobile:', /Mobile|Android|iPhone|iPad/i.test(navigator.userAgent));
      
      const response = await axios.post(
        `${API}/auth/session-data`,
        {},
        {
          headers: {
            'X-Session-ID': sessionId
          },
          withCredentials: true
        }
      );

      setUser(response.data.user);
      setHasAccess(response.data.has_access || true);
      
      // Clean URL and navigate
      window.history.replaceState({}, document.title, '/dashboard');
      navigate('/dashboard');
    } catch (error) {
      console.error('Session processing error:', error);
      console.error('Error details:', error.response?.data);
      toast.error(error.response?.data?.detail || 'Authentication failed. Please try again.');
      
      // Clean the hash and stay on landing page
      window.history.replaceState({}, document.title, '/');
      setIsProcessing(false);
    }
  };

  const handleLogin = () => {
    // Detect if mobile (including Samsung browser)
    const isMobile = /Mobile|Android|iPhone|iPad|SamsungBrowser/i.test(navigator.userAgent);
    
    // Use intermediate redirect page for mobile to handle hash issues
    const redirectPath = isMobile ? '/auth-redirect.html' : '/';
    const redirectUrl = `${window.location.origin}${redirectPath}`;
    
    console.log('Login redirect:', {
      isMobile,
      redirectUrl,
      userAgent: navigator.userAgent
    });
    
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const demoResults = {
    'Hydrochloric Acid': {
      status: 'not_compatible',
      color: 'red',
      report: `🚨 **SAFETY ALERT** 🚨

**COMPATIBILITY STATUS:** NOT COMPATIBLE

**CONFIDENCE LEVEL:** High (extensively documented, multiple authoritative sources)

**CRITICAL WARNING:**
Hydrochloric Acid (HCl) is NOT suitable for use with Stainless Steel 316L in the vast majority of situations. It is one of the most aggressive acids towards stainless steels and will cause severe corrosion, pitting, and stress corrosion cracking.

**WHY NOT COMPATIBLE:**
• Chloride ions (Cl⁻) from HCl cause rapid pitting corrosion
• Attacks the passive chromium oxide layer on SS 316L
• Causes stress corrosion cracking even at low concentrations
• Corrosion accelerates with temperature and concentration
• Even dilute solutions (<5%) cause significant corrosion over time

**CORROSION DATA:**
- At 5% HCl, 20°C: Corrosion rate ~2-5 mm/year (severe)
- At 10% HCl, 20°C: Corrosion rate ~10-20 mm/year (extreme)
- At 20% HCl, 20°C: Corrosion rate >50 mm/year (catastrophic failure)
- Higher temperatures exponentially increase corrosion rates

**CAS NUMBER:** 7647-01-0

**UN NUMBER:** UN1789 (Hydrochloric acid solution)

**T-CODE:** T8 (Tank requirements for corrosive liquids per IMDG/ADR)
⚠️ **IMPORTANT:** T-Codes vary by Packing Group (I, II, or III). Always verify the correct Packing Group for the specific concentration before finalizing the T-Code. Consult IMDG Code, ADR, or USDOT regulations.

**NITROGEN TRANSPORT:** Not required for HCl solutions.

**GAS TIGHT SEAL:** YES - Required. HCl solutions release highly corrosive vapors (HCl gas) that damage equipment and pose serious health risks.

**RECOMMENDED MATERIALS (NOT SS 316L):**
- PTFE/Teflon (all concentrations and temperatures)
- PVDF (Kynar) - up to 60°C
- High-density polyethylene (HDPE) - for dilute solutions
- Hastelloy C-276 - for high-temperature applications
- Glass-lined steel - for processing equipment
- Rubber-lined steel - for storage tanks

**IF ACCIDENTAL EXPOSURE OCCURS:**
1. Immediately flush with large amounts of water (minimum 15 minutes)
2. Neutralize with sodium bicarbonate solution (baking soda)
3. Rinse again with water
4. Inspect for pitting and surface damage
5. Replace equipment if corrosion is visible

**SAFETY NOTES:**
- DO NOT USE SS 316L with hydrochloric acid in ANY concentration
- Failure to heed this warning can result in:
  * Sudden equipment failure
  * Chemical spills and environmental contamination
  * Personal injury from corrosive chemical exposure
  * Property damage
  * Regulatory violations

**DATA SOURCES:** 
- NACE International Corrosion Data Survey
- ASTM G48 (Standard Test Methods for Pitting and Crevice Corrosion)
- Cole-Parmer Chemical Compatibility Database
- Nickel Development Institute (NiDI) Publication 14 040

**ENGINEERING RECOMMENDATION:**
Select alternative materials specifically rated for hydrochloric acid service. Consult a corrosion engineer for material selection in critical applications.

**DISCLAIMER:** This analysis is based on established corrosion data and industry standards. This is NOT COMPATIBLE regardless of conditions. Always consult official Safety Data Sheets and qualified professionals before selecting materials for chemical handling.`
    },
    'Sodium Hydroxide': {
      status: 'compatible',
      color: 'green',
      report: `**COMPATIBILITY STATUS:** COMPATIBLE

**CONFIDENCE LEVEL:** High (well-documented)

**SUMMARY:**
Sodium Hydroxide (NaOH, Caustic Soda) demonstrates excellent compatibility with Stainless Steel 316L across a wide range of concentrations and temperatures. This is one of the preferred materials for handling caustic solutions.

**KEY FINDINGS:**
• Excellent resistance to all concentrations at ambient temperature
• Good resistance at elevated temperatures up to 100°C
• Minimal corrosion even with concentrated solutions (50%)
• Stress corrosion cracking risk is low with proper design

**DETAILED ANALYSIS:**
- Corrosion rate at 50% NaOH, 20°C: <0.02 mm/year (excellent)
- Corrosion rate at 50% NaOH, 80°C: <0.1 mm/year (very good)
- Pitting resistance: Excellent due to passive film formation
- Temperature effect: Minimal up to boiling point

**UN NUMBER:** UN1823 (Sodium hydroxide, solid) / UN1824 (Sodium hydroxide, solution)

**T-CODE:** T7 (Tank requirements for alkaline corrosive liquids per IMDG/ADR)
⚠️ **IMPORTANT:** T-Codes vary by Packing Group (I, II, or III). Always verify the correct Packing Group before finalizing the T-Code. Consult IMDG Code, ADR, or USDOT regulations.

**NITROGEN TRANSPORT:** Not required. NaOH is chemically stable and does not react with air under normal conditions.

**GAS TIGHT SEAL:** Not strictly required but recommended for concentrated solutions to prevent moisture absorption and spillage.

**CLEANING PROCESS:**
1. Rinse thoroughly with water to remove all caustic residues
2. Neutralize with dilute acid (e.g., 5% acetic acid) if needed
3. Final rinse with deionized water
4. Air dry or wipe with clean cloth

**ADVANTAGES WITH SS 316L:**
• Passive oxide layer enhanced by alkaline environment
• No chloride stress corrosion cracking risk
• Long service life expected
• Low maintenance requirements

**SAFETY NOTES:**
- Proper ventilation recommended for hot caustic solutions
- Avoid mixing with acids or oxidizers
- Standard chemical handling procedures apply

**DATA SOURCES:** NACE SP0108, ASTM A923, Industry corrosion databases

**DISCLAIMER:** This analysis is for informational purposes. Verify with SDS and conduct testing for specific applications.`
    },
    'Acetone': {
      status: 'compatible',
      color: 'green',
      report: `**COMPATIBILITY STATUS:** COMPATIBLE

**CONFIDENCE LEVEL:** High (extensively documented)

**SUMMARY:**
Acetone (2-Propanone, CH₃COCH₃) is fully compatible with Stainless Steel 316L. No corrosion or degradation occurs even with pure acetone at any practical temperature.

**KEY FINDINGS:**
• Zero measurable corrosion rate at all temperatures
• No chemical reaction with stainless steel
• Suitable for long-term storage and processing
• No special precautions needed regarding material compatibility

**DETAILED ANALYSIS:**
- Corrosion rate: <0.001 mm/year (essentially zero)
- Chemical stability: Acetone does not react with chromium, nickel, or molybdenum
- Temperature range: Suitable from -20°C to boiling point (56°C)
- Purity: Both pure and water-mixed acetone are compatible

**UN NUMBER:** UN1090 (Acetone)

**T-CODE:** T4 (Tank requirements for flammable liquids per IMDG/ADR)
⚠️ **IMPORTANT:** T-Codes vary by Packing Group (I, II, or III). Always verify the correct Packing Group before finalizing the T-Code. Consult IMDG Code, ADR, or USDOT regulations.

**NITROGEN TRANSPORT:** Recommended for large bulk storage to prevent flammability hazards and reduce vapor space. Nitrogen blanketing prevents ignition risks from static electricity or external ignition sources.

**GAS TIGHT SEAL:** YES - Highly recommended. Acetone is:
- Highly volatile (boiling point 56°C)
- Flammable (flash point -20°C)
- Prone to evaporation losses
A gas-tight seal minimizes vapor emissions and fire risk.

**CLEANING PROCESS:**
1. Drain all acetone from the system
2. Rinse with water (acetone is fully miscible with water)
3. Final rinse with deionized water or leave to air dry
4. No special neutralization needed

**HANDLING NOTES:**
• Primary concern is flammability, not corrosion
• Eliminate ignition sources
• Ensure adequate ventilation
• Use explosion-proof equipment in enclosed spaces
• Acetone dissolves many plastics and rubbers - use appropriate seals

**COMPATIBILITY WITH OTHER MATERIALS:**
- SS 316L: Excellent ✓
- Most plastics: Poor (use PTFE, HDPE)
- Viton seals: Good
- Buna-N rubber: Poor

**DATA SOURCES:** NIST Chemistry WebBook, Material Safety Data Sheets, ASTM standards

**DISCLAIMER:** While SS 316L compatibility is excellent, focus on flammability and safety protocols when handling acetone.`
    }
  };

  const handleDemoSearch = (chemical) => {
    setDemoChemical(chemical);
    setShowDemoModal(true);
  };

  if (isProcessing) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
        <p className="text-white mt-4 text-center px-4">
          {/Mobile|Android|iPhone|iPad|SamsungBrowser/i.test(navigator.userAgent) 
            ? 'Authenticating on mobile... This may take a moment...' 
            : 'Authenticating...'}
        </p>
        {showManualAuth && (
          <div className="mt-6 max-w-md mx-auto px-4">
            <Card className="glass border-white/20">
              <CardContent className="pt-6 space-y-3">
                <p className="text-white text-sm text-center">
                  Having trouble on mobile? Try these steps:
                </p>
                <ol className="text-white text-xs space-y-2 text-left">
                  <li>1. Use Chrome, Safari, or Samsung Internet browser (not Instagram/Facebook browser)</li>
                  <li>2. Clear your browser cache</li>
                  <li>3. Allow pop-ups and redirects</li>
                  <li>4. Click the login button again</li>
                </ol>
                <Button
                  onClick={() => {
                    setIsProcessing(false);
                    setShowManualAuth(false);
                    window.location.href = '/';
                  }}
                  className="w-full bg-white text-purple-700 hover:bg-gray-100 mt-4"
                >
                  Try Again
                </Button>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Hero Background Image */}
      <div 
        className="absolute inset-0 z-0"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1588011930968-eadac80e6a5a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwxfHxjaGVtaWNhbCUyMHBsYW50fGVufDB8fHx8MTc2MTMxMDkwNnww&ixlib=rb-4.1.0&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          filter: 'brightness(0.5) contrast(1.1)'
        }}
      />
      
      {/* Gradient Overlay */}
      <div className="absolute inset-0" style={{
        background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.7) 0%, rgba(118, 75, 162, 0.7) 100%)',
        zIndex: 1
      }} />

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center justify-center min-h-screen px-4 py-12">
        <div className="max-w-4xl w-full text-center space-y-8">
          {/* Logo/Title */}
          <div className="space-y-4" data-testid="landing-hero">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Chem-Steel 316L<br />Compatibility Checker
            </h1>
            <p className="text-lg sm:text-xl text-white/90 max-w-2xl mx-auto" style={{ fontFamily: 'Inter, sans-serif' }}>
              AI-powered chemical compatibility analysis for Stainless Steel 316L.
              Get instant, detailed reports on chemical safety, transport codes, and handling procedures.
            </p>
          </div>

          {/* Features Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
            <div className="glass p-6 space-y-3">
              <div className="text-3xl">🔬</div>
              <h3 className="text-xl font-semibold text-white">AI Analysis</h3>
              <p className="text-white/80 text-sm">Real-time web data for over 20,000 chemicals</p>
            </div>
            <div className="glass p-6 space-y-3">
              <div className="text-3xl">📊</div>
              <h3 className="text-xl font-semibold text-white">Detailed Reports</h3>
              <p className="text-white/80 text-sm">Comprehensive compatibility reports with transport codes</p>
            </div>
            <div className="glass p-6 space-y-3">
              <div className="text-3xl">💾</div>
              <h3 className="text-xl font-semibold text-white">Save & Track</h3>
              <p className="text-white/80 text-sm">Bookmark chemicals and track search history</p>
            </div>
          </div>

          {/* Try Example Searches */}
          <div className="mt-12 glass p-6">
            <h3 className="text-xl font-semibold text-white mb-4 text-center">Try Example Searches</h3>
            <p className="text-white/80 text-sm text-center mb-4">
              See instant demo results (no signup required)
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Button
                onClick={() => handleDemoSearch('Hydrochloric Acid')}
                variant="outline"
                className="bg-white/10 text-white border-white/20 hover:bg-white/20"
              >
                Try: Hydrochloric Acid
              </Button>
              <Button
                onClick={() => handleDemoSearch('Sodium Hydroxide')}
                variant="outline"
                className="bg-white/10 text-white border-white/20 hover:bg-white/20"
              >
                Try: Sodium Hydroxide
              </Button>
              <Button
                onClick={() => handleDemoSearch('Acetone')}
                variant="outline"
                className="bg-white/10 text-white border-white/20 hover:bg-white/20"
              >
                Try: Acetone
              </Button>
            </div>
          </div>

          {/* CTA Button */}
          <div className="mt-12">
            <Button
              data-testid="login-button"
              onClick={handleLogin}
              size="lg"
              className="bg-white text-purple-700 hover:bg-gray-100 px-8 py-6 text-lg font-semibold rounded-full btn-hover shadow-2xl"
            >
              Get Started with Google
            </Button>
          </div>

          {/* Trial Info */}
          <div className="mt-8 space-y-3">
            <div className="glass p-4 inline-block">
              <p className="text-white text-sm">
                🎉 <strong>10 Days Free Trial</strong> - Start analyzing chemicals today!
              </p>
            </div>
            
            {/* Mobile Note */}
            {/Mobile|Android|iPhone|iPad|SamsungBrowser/i.test(navigator.userAgent) && (
              <div className="glass p-4 inline-block">
                <p className="text-white text-xs">
                  📱 <strong>Mobile users:</strong> Chrome, Safari, or Samsung Internet supported
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="relative z-10 glass border-t border-white/20 mt-8">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-white/80 text-sm">
            <div className="flex items-center gap-2">
              <span>© {new Date().getFullYear()} Chem-Steel 316L Compatibility Checker</span>
            </div>
            
            <div className="flex flex-wrap items-center justify-center gap-4">
              <a 
                href="/whats-new" 
                className="hover:text-white underline"
              >
                What's New
              </a>
              <span>•</span>
              <a 
                href="/faq" 
                className="hover:text-white underline"
              >
                FAQ
              </a>
              <span>•</span>
              <a 
                href="/terms" 
                className="hover:text-white underline"
              >
                Terms of Service
              </a>
              <span>•</span>
              <a 
                href="/privacy" 
                className="hover:text-white underline"
              >
                Privacy Policy
              </a>
              <span>•</span>
              <a 
                href="mailto:info@corrosioncheck.com" 
                className="hover:text-white underline"
              >
                info@corrosioncheck.com
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Demo Results Modal */}
      <Dialog open={showDemoModal} onOpenChange={setShowDemoModal}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto bg-white">
          <DialogHeader>
            <DialogTitle className="text-2xl" style={{ fontFamily: 'Space Grotesk' }}>
              Demo Analysis: {demoChemical}
            </DialogTitle>
            <DialogDescription>
              This is a sample analysis report. Sign up for a free trial to analyze any chemical!
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Compatibility Status Box */}
            {demoChemical && demoResults[demoChemical] && (
              <>
                <div className={`p-6 rounded-lg ${
                  demoResults[demoChemical].color === 'red' ? 'bg-red-500' :
                  demoResults[demoChemical].color === 'yellow' ? 'bg-yellow-500' :
                  'bg-green-500'
                } text-white`}>
                  <h3 className="text-2xl font-bold text-center">
                    {demoResults[demoChemical].status === 'not_compatible' ? 'NOT COMPATIBLE' :
                     demoResults[demoChemical].status === 'caution' ? 'MERIT CAUTION / CONDITIONALLY COMPATIBLE' :
                     'COMPATIBLE'}
                  </h3>
                </div>

                {/* Analysis Report */}
                <div className="prose prose-sm max-w-none">
                  <div className="whitespace-pre-wrap text-gray-800 leading-relaxed">
                    {demoResults[demoChemical].report}
                  </div>
                </div>

                {/* CTA */}
                <div className="bg-purple-50 p-6 rounded-lg text-center space-y-4">
                  <p className="text-gray-700 font-semibold">
                    Want to analyze any chemical with AI-powered real-time data?
                  </p>
                  <Button
                    onClick={handleLogin}
                    className="bg-purple-700 hover:bg-purple-800 text-white px-8 py-6 text-lg"
                  >
                    Start Free 10-Day Trial
                  </Button>
                  <p className="text-gray-600 text-sm">
                    No credit card required • Unlimited analyses during trial
                  </p>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LandingPage;
